/**
 * Plan upgrade hierarchy — which plan comes next within each scope.
 *
 * 1:1 port of legacy `src/lib/features/hierarchy.ts` (D20 LOCK / "no reinvention").
 *
 * Used to auto-compute `upgradeTo` for a feature: when a feature is locked on
 * plan X, find the next plan Y in the hierarchy that grants access, and use Y
 * as the upgrade target.
 */

import { PLAN_FEATURE_DEFAULTS } from "./catalog.js";

/** Each plan's successor within its tier. Terminal plans (top of tier) have no successor. */
export const PLAN_HIERARCHY: Record<string, string[]> = {
  // User scope
  user_free: ["user_plus"],
  user_plus: [],

  // Provider scope
  provider_free: ["provider_pro", "provider_expert"],
  provider_pro: ["provider_expert"],
  provider_expert: [],

  // Facility scope
  facility_starter: ["facility_professional", "facility_enterprise"],
  facility_professional: ["facility_enterprise"],
  facility_enterprise: [],

  // Business scope
  business_free: ["business_basic", "business_pro"],
  business_basic: ["business_pro"],
  business_pro: [],
};

/**
 * Given a plan and a feature, find the first upgrade target where the feature
 * is fully usable (`included` or `limited` with positive limit). Returns null
 * if no higher plan grants the feature.
 */
export function computeUpgradeTarget(planCode: string, featureKey: string): string | null {
  const successors = PLAN_HIERARCHY[planCode] ?? [];
  for (const next of successors) {
    const access = PLAN_FEATURE_DEFAULTS[next]?.[featureKey];
    if (!access) continue;
    if (access.access === "included") return next;
    if (access.access === "limited") {
      const limit = access.limit ?? 0;
      if (limit !== 0) return next; // positive or -1 (unlimited) counts
    }
  }
  return null;
}
